import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { findSimilarClients } from '../lib/clientMatch.js';
import { Icon } from './Icon.jsx';
import { useToast } from './Toast.jsx';

// v2.4 — admin-managed client directory.
//
// Approver / super-approver only. Renders a searchable, no-frills
// table of active clients and a single Add client button. Listing
// deliberately runs without per-client entry counts: the brief
// forbids a COUNT join, and the directory is a maintenance surface,
// not an analytics one.
//
// The Add dialog couples a hard duplicate guard (case-insensitive
// exact match) with a soft, advisory "similar clients exist" hint
// powered by `findSimilarClients` from lib/clientMatch.js. A
// near-match never blocks submit — admins routinely need to add
// genuinely-distinct names that happen to read alike.

// Format an ISO date / ISO timestamp into "MMM YYYY" for the Added
// column. Falls back to the raw string when the input doesn't parse,
// so seed rows with empty / non-standard dates degrade gracefully.
export function formatAddedMonth(input) {
  if (!input) return '';
  const s = String(input);
  // Accept either YYYY-MM-DD or full ISO timestamps.
  const d = new Date(s.length >= 10 ? s.slice(0, 10) : s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

// Pure helper exposed for tests — the duplicate check that gates the
// Add button. Case-insensitive trim match against the active list.
export function isDuplicateName(name, existing) {
  const target = String(name || '').trim().toLowerCase();
  if (!target) return false;
  return (existing || []).some(
    (c) => String(c?.name || '').toLowerCase() === target,
  );
}

export function ClientDirectory() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const toast = useToast();

  const load = useCallback(
    async (q) => {
      setLoading(true);
      try {
        const data = await api.listClients(q || '');
        setClients(data.clients || []);
      } catch {
        toast('Could not load clients', 'error');
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  // Initial load on mount; the brief notes server-side q filtering is
  // load-bearing for scale, but we also debounce locally so an admin
  // typing fast doesn't fire a dozen requests.
  useEffect(() => {
    load('');
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(query), 220);
    return () => clearTimeout(t);
  }, [query, load]);

  const handleAdded = (client) => {
    toast(`Added “${client.name}” to the client list`);
    setAddOpen(false);
    // Re-read so the new row picks up the server-assigned id + date.
    load(query);
  };

  const filteredCount = clients.length;

  return (
    <div
      style={{
        maxWidth: 880,
        margin: '0 auto',
        padding: '32px 24px 96px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div style={{ flex: 1 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 700,
              color: 'var(--sp-fg-1)',
              letterSpacing: '-0.01em',
            }}
          >
            Client directory
          </h1>
          <p
            style={{
              margin: '6px 0 0',
              color: 'var(--sp-fg-2)',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            The clients everyone can pick when logging time. Only admins
            and approvers manage this list.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setAddOpen(true)}
        >
          <Icon name="plus" size={14} /> Add client
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <span style={{ color: 'var(--sp-fg-3)', fontSize: 13 }}>
          {loading
            ? 'Loading…'
            : `${filteredCount} ${filteredCount === 1 ? 'client' : 'clients'}`}
        </span>
        <div style={{ flex: 1 }} />
        <div
          className="tt-input"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            width: 260,
            paddingLeft: 10,
          }}
        >
          <span style={{ color: 'var(--sp-fg-3)', display: 'inline-flex' }}>
            <Icon name="search" size={14} />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter clients…"
            style={{
              flex: 1,
              border: 0,
              outline: 0,
              background: 'transparent',
              fontSize: 13,
              color: 'var(--sp-fg-1)',
              padding: '8px 0',
            }}
          />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
          }}
        >
          <thead>
            <tr style={{ background: 'var(--sp-bg-muted)' }}>
              <th style={thStyle}>Client</th>
              <th style={{ ...thStyle, width: 140 }}>Added</th>
              <th style={{ ...thStyle, textAlign: 'right', width: 240 }}>
                Added by
              </th>
            </tr>
          </thead>
          <tbody>
            {!loading && clients.length === 0 && (
              <tr>
                <td colSpan={3} style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--sp-fg-3)' }}>
                  {query.trim()
                    ? `No clients match “${query.trim()}”.`
                    : 'No clients yet — use Add client to seed the list.'}
                </td>
              </tr>
            )}
            {clients.map((c) => (
              <tr key={c.id || c.name} style={{ borderTop: '1px solid var(--sp-border-soft)' }}>
                <td style={tdStyle}>
                  <span style={{ fontWeight: 500, color: 'var(--sp-fg-1)' }}>
                    {c.name}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: 'var(--sp-fg-2)' }}>
                  {formatAddedMonth(c.addedAt)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--sp-fg-2)' }}>
                  {c.addedBy || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <ClientAddDialog
          existing={clients}
          onCancel={() => setAddOpen(false)}
          onAdded={handleAdded}
        />
      )}
    </div>
  );
}

const thStyle = {
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--sp-fg-3)',
  padding: '10px 16px',
};

const tdStyle = {
  padding: '12px 16px',
  verticalAlign: 'middle',
};

export function ClientAddDialog({ existing, onCancel, onAdded }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = React.useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, []);

  const trimmed = name.trim();
  const duplicate = useMemo(
    () => isDuplicateName(trimmed, existing),
    [trimmed, existing],
  );
  const similar = useMemo(
    () => (trimmed && !duplicate ? findSimilarClients(trimmed, existing) : []),
    [trimmed, duplicate, existing],
  );
  const canSubmit = !!trimmed && !duplicate && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.addClient(trimmed);
      onAdded(res.client);
    } catch (err) {
      if (err.status === 409) {
        setError('A client with that name already exists.');
      } else if (err.status === 403) {
        setError('You don’t have permission to add clients.');
      } else {
        setError('Could not add client. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
    if (e.key === 'Escape' && !busy) onCancel();
  };

  return (
    <div className="tt-modal-backdrop" onClick={busy ? undefined : onCancel}>
      <div
        className="tt-modal"
        style={{ maxWidth: 440, marginTop: '10vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '24px 24px 8px' }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
            Add client
          </h3>
          <p
            style={{
              margin: '6px 0 16px',
              color: 'var(--sp-fg-2)',
              fontSize: 13,
            }}
          >
            New clients are immediately available in the picker for everyone.
          </p>
          <label className="tt-field-label">Client name</label>
          <input
            ref={inputRef}
            type="text"
            className={
              'tt-input ' + (duplicate || error ? 'is-error' : '')
            }
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={onKeyDown}
            placeholder="e.g. Foodpanda"
            maxLength={120}
            disabled={busy}
          />
          {duplicate && (
            <div className="tt-error-msg">
              <Icon name="alert" size={12} /> A client with that name already
              exists.
            </div>
          )}
          {error && !duplicate && (
            <div className="tt-error-msg">
              <Icon name="alert" size={12} /> {error}
            </div>
          )}
          {!duplicate && similar.length > 0 && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 12px',
                background: 'var(--sp-apricot-50)',
                border: '1px solid var(--sp-apricot-100)',
                borderRadius: 8,
                color: 'var(--sp-apricot-ink)',
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Similar clients already exist
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {similar.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => setName(s.name)}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: '#fff',
                      border: '1px solid var(--sp-apricot-100)',
                      color: 'var(--sp-apricot-ink)',
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div
          style={{
            padding: 16,
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <button
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={!canSubmit}
          >
            {busy && (
              <span className="tt-spinner sm" style={{ marginRight: 6 }} />
            )}
            {busy ? 'Adding…' : 'Add client'}
          </button>
        </div>
      </div>
    </div>
  );
}
